import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { normalizeCameraFromApi, getCameraStableId } from '../../utils/ThreeJsUtils/sceneCameraUtils';

// import { updateCamera } from '@react-three/fiber/dist/declarations/src/core/utils';

export const get_grid_assets = createAsyncThunk(
  'grid_assets',
  (res, { rejectWithValue }) => {
    return res;
  }
);

export const add_new_assets = createAsyncThunk('add_assets', (data) => {
  return data;
});

export const remove_assets_by_id = createAsyncThunk(
  'remove_assets_by_id',
  (data) => {
    return data;
  }
);

const assetIdMatchesPayload = (item, payload) => {
  const deleteIds = [payload?.id, payload?.url].filter(Boolean);
  if (!deleteIds.length) return false;
  const itemIds = [
    item?._id,
    item?.id,
    item?.url,
    item?.name,
    item?.temp_file_name,
    item?.text_material?.id,
    item?.text_material?._id,
    item?.userData?.text_material?.id,
  ].filter(Boolean);
  return deleteIds.some((deleteId) => itemIds.includes(deleteId));
};
export const selected_object = createAsyncThunk('selected_object', (data) => {
  return data;
});

const initialState = {
  assets_data: {},
  assets_loading: false,
  assets_image_data: [],
  assets_video_data: [],
  assets_audio_data: [],
  assets_mesh_data: [],
  assets_hotspot_data: [],
  empty: [],
  select_object: {},
  is_glb_image_editoropen: false,
  is_video_editoropen: false,
  is_audio_editoropen: false,
  openEditorType: null,

  // other changes by Naveen

  imageUrls: [],
  modelUrls: [],
  videoUrls: [],
  audioUrls: [],

  imageFileName: [],
  modelFileName: [],
  videoFileName: [],
  audioFileName: [],

  sceneMeshes: [],
  sceneHotspots: [],
  sceneImages: [],
  sceneVideos: [],
  sceneAudios: [],
  sceneText: [],
  sceneElements: [],

  imageScales: [],
  videoScales: [],
  audioScales: [],

  allIntersectedObjects: [],
  selectedMeshesForGrouping: [],
  /** Meshes highlighted when a GLB material is picked in the material list (outline/bbox only). */
  materialHighlightMeshes: [],

  light_data: [], // Start with no lights by default

  // light_data: [], // Now it's an array to hold multiple lights

  openModals: {
    text: false,
    upload: false,
    preview_share: false,
    library: false,
  },

  // Add events state
  events: [],
  // cameraType: 'perspective', // Default camera type

  camera_properties: [],
};

const GridAssetsSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    set_is_glb_image_editoropen: (state, action) => {
      state.is_glb_image_editoropen = action.payload;
    },

    set_is_video_editoropen: (state, action) => {
      state.is_video_editoropen = action.payload;
    },

    set_is_audio_editoropen: (state, action) => {
      state.is_audio_editoropen = action.payload;
    },
    set_openEditorType: (state, action) => {
      state.openEditorType = action.payload;
    },

    // new Reducers by Naveen

    addImage: (state, action) => {
      state.imageUrls.push(action.payload.url);
      state.imageFileName.push(action.payload.name);
    },
    addModel: (state, action) => {
      state.modelUrls.push(action.payload.url);
      state.modelFileName.push(action.payload.name);
    },
    addVideo: (state, action) => {
      state.videoUrls.push(action.payload.url);
      state.videoFileName.push(action.payload.name);
    },
    addAudio: (state, action) => {
      state.audioUrls.push(action.payload.url);
      state.audioFileName.push(action.payload.name);
    },

    addSceneMesh: (state, action) => {
      state.sceneMeshes.push(action.payload);
    },
    addSceneHotspot: (state, action) => {
      const hotspot = action.payload;
      const already =
        state.sceneHotspots.includes(hotspot) ||
        (hotspot?.name &&
          state.sceneHotspots.some((h) => h.name === hotspot.name));
      if (!already) state.sceneHotspots.push(hotspot);
    },
    addSceneImage: (state, action) => {
      state.sceneImages.push(action.payload);
    },
    addSceneVideo: (state, action) => {
      state.sceneVideos.push(action.payload);
    },
    removeSceneVideo: (state, action) => {
      state.sceneVideos = state.sceneVideos.filter(
        (video) => video.uuid !== action.payload.uuid
      );
    },
    addSceneAudio: (state, action) => {
      state.sceneAudios.push(action.payload);
    },
    addSceneText: (state, action) => {
      state.sceneText.push(action.payload);
    },
    addSceneElement: (state, action) => {
      state.sceneElements.push(action.payload);
    },

    removeSceneMesh: (state, action) => {
      state.sceneMeshes = state.sceneMeshes.filter(
        (mesh) => mesh !== action.payload
      );
    },
    removeSceneHotspot: (state, action) => {
      state.sceneHotspots = state.sceneHotspots.filter(
        (hotspot) => hotspot !== action.payload
      );
    },

    setImageScales: (state, action) => {
      state.imageScales.push(action.payload);
    },
    setVideoScales: (state, action) => {
      state.videoScales.push(action.payload);
    },
    setAudioScales: (state, action) => {
      state.audioScales.push(action.payload);
    },

    setPushIntersectedObject: (state, action) => {
      const pushUnique = (obj) => {
        if (!obj) return;
        // Deduplicate by identity
        if (!state.allIntersectedObjects.includes(obj)) {
          state.allIntersectedObjects.push(obj);
        }
      };
      if (Array.isArray(action.payload)) {
        action.payload.forEach(pushUnique);
      } else {
        pushUnique(action.payload);
      }
    },
    removeIntersectedObject: (state, action) => {
      state.allIntersectedObjects = state.allIntersectedObjects.filter(
        (obj) => obj !== action.payload
      );
    },
    clearAllIntersectedObjects: (state) => {
      state.allIntersectedObjects = [];
    },

    lightDataUpdate: (state, action) => {
      const { payload } = action;
      if (payload && payload.actionType === 'add') {
        // Accept all properties, including range, angle, width, height
        state.light_data.push({ ...payload.light });
      } else if (payload && payload.actionType === 'update') {
        const updated = state.light_data.map((light) =>
          light.id === payload.light.id ? { ...light, ...payload.light } : light
        );
        if (JSON.stringify(updated) !== JSON.stringify(state.light_data)) {
          state.light_data = updated;
        }
      } else if (payload && payload.actionType === 'remove') {
        const filtered = state.light_data.filter(
          (light) => light.id !== payload.id
        );
        if (filtered.length !== state.light_data.length) {
          state.light_data = filtered;
        }
      } else if (Array.isArray(payload)) {
        // For bulk replace (e.g., loading from storage)
        if (JSON.stringify(payload) !== JSON.stringify(state.light_data)) {
          state.light_data = payload;
        }
      } else if (payload && payload.id) {
        const updated = state.light_data.map((light) =>
          light.id === payload.id ? { ...light, ...payload } : light
        );
        if (JSON.stringify(updated) !== JSON.stringify(state.light_data)) {
          state.light_data = updated;
        }
      }
    },

    // In your gridSlice.js (if you don't have this action)
    setSceneMeshes: (state, action) => {
      state.sceneMeshes = action.payload;
    },

    /**
     * Sync material_settings from the live Three.js pivot back into the Redux
     * sceneMeshes record.  Called after UpdateMaterial mutates
     * pivot.userData.material_settings so that the next publish always reads
     * the latest user edits — even on first publish of a library/sketchfab asset.
     *
     * payload: { id: string, material_settings: object }
     *   id — any of _id | name | temp_file_name that identifies the mesh record
     */
    updateSceneMeshMaterialSettings: (state, action) => {
      const { id, material_settings } = action.payload || {};
      if (!id || !material_settings) return;
      state.sceneMeshes = state.sceneMeshes.map((m) => {
        const matches =
          m._id === id ||
          m.name === id ||
          m.temp_file_name === id;
        if (!matches) return m;
        return { ...m, material_settings: { ...(m.material_settings || {}), ...material_settings } };
      });
    },

    restoreGridHistorySnapshot: (state, action) => {
      const payload = action.payload || {};
      if (payload.sceneMeshes) state.sceneMeshes = payload.sceneMeshes;
      if (payload.sceneHotspots) state.sceneHotspots = payload.sceneHotspots;
      if (payload.sceneImages) state.sceneImages = payload.sceneImages;
      if (payload.sceneVideos) state.sceneVideos = payload.sceneVideos;
      if (payload.sceneAudios) state.sceneAudios = payload.sceneAudios;
      if (payload.sceneText) state.sceneText = payload.sceneText;
      if (payload.sceneElements) state.sceneElements = payload.sceneElements;
      if (payload.allIntersectedObjects) {
        state.allIntersectedObjects = payload.allIntersectedObjects;
      }
      if (payload.assets_data) state.assets_data = payload.assets_data;
      if (payload.light_data) state.light_data = payload.light_data;
      if (payload.camera_properties) {
        state.camera_properties = payload.camera_properties;
      }
      if (payload.events) state.events = payload.events;
      if (payload.selectedObjectId !== undefined) {
        const id = payload.selectedObjectId;
        if (!id) {
          state.select_object = null;
        } else {
          const pools = [
            ...state.sceneMeshes,
            ...state.sceneImages,
            ...state.sceneVideos,
            ...state.sceneAudios,
            ...state.sceneText,
            ...state.sceneHotspots,
            ...state.allIntersectedObjects,
          ];
          const fromPool = pools.find(
            (obj) =>
              obj &&
              (obj.name === id ||
                obj._id === id ||
                obj.id === id ||
                obj.url === id ||
                obj.temp_file_name === id)
          );
          const fromLight = (state.light_data || []).find(
            (light) =>
              light.id === id ||
              light.name === id ||
              light._id === id ||
              light.temp_file_name === id
          );
          const fromCamera = (state.camera_properties || []).find(
            (cam) =>
              cam._id === id ||
              cam.id === id ||
              cam.name === id ||
              cam.temp_file_name === id
          );
          state.select_object = fromPool || fromLight || fromCamera || null;
        }
      }
    },

    setSelectedMeshesForGrouping(state, action) {
      state.selectedMeshesForGrouping = action.payload;
    },

    setMaterialHighlightMeshes(state, action) {
      state.materialHighlightMeshes = action.payload;
    },

    // Add event reducers
    addEvent: (state, action) => {
      state.events.push(action.payload);
    },

    removeEvent: (state, action) => {
      state.events = state.events.filter(
        (event) => event.id !== action.payload
      );
    },

    clearEvents: (state) => {
      state.events = [];
    },
    addCamera: (state, action) => {
      const cam = normalizeCameraFromApi({ ...action.payload });
      state.camera_properties.push(cam);
    },

    removeCamera(state, action) {
      const removeId = action.payload;
      state.camera_properties = state.camera_properties.filter(
        (c) => c._id !== removeId && c.id !== removeId
      );
    },

    updateCameraType: (state, action) => {
      state.cameraType = action.payload;
    },

    // Light actions for multiple lights
    // addLight: (state, action) => {
    //   state.light_data.push(action.payload);
    // },

    addLight: (state, action) => {
      const newLight = { ...action.payload };

      // ✅ was state.lights — should be state.light_data
      const sameTypeLights = state.light_data.filter(
        (l) => l.light_type === newLight.light_type
      );

      if (sameTypeLights.length === 0) {
        newLight.name = newLight.light_type;
      } else {
        const maxNum = sameTypeLights.reduce((max, l) => {
          const match = l.name.match(/(\d+)$/);
          return match ? Math.max(max, parseInt(match[1])) : Math.max(max, 0);
        }, 0);

        newLight.name = `${newLight.light_type} ${maxNum + 1}`;
      }

      // ✅ was state.lights.push — should be state.light_data.push
      state.light_data.push(newLight);
    },
    updateLight: (state, action) => {
      state.light_data = state.light_data.map((light) =>
        light.id === action.payload.id ? { ...light, ...action.payload } : light
      );
    },
    removeLight: (state, action) => {
      state.light_data = state.light_data.filter(
        (light) => light.id !== action.payload
      );
    },
    addLightToOtherObject: (state, action) => {
      const { id } = action.payload;
      state.light_data = state.light_data.map((light) =>
        light.id === id ? { ...light, otherObject: true } : light
      );
      const light_data = state.light_data.find((light) => light.id === id);
    },

    // clearAllAssets: () => initialState,
    clearAssets: (state, action) => {
      const keysToClear = action.payload; // Array of keys to clear or the string "All"

      if (keysToClear === 'All') {
        // Reset the entire state
        Object.assign(state, initialState);
      } else if (Array.isArray(keysToClear)) {
        keysToClear.forEach((key) => {
          if (key in state) {
            state[key] = Array.isArray(initialState[key])
              ? [] // reset array
              : (initialState[key] ?? null); // reset primitive or object to its initial value
          }
        });
      }
    },

    removeSceneObject: (state, action) => {
      const objectData = action.payload;

      state.sceneMeshes = state.sceneMeshes.filter((obj) => obj !== objectData);
      state.sceneHotspots = (state.sceneHotspots || []).filter(
        (obj) => obj !== objectData
      );
      state.sceneImages = state.sceneImages.filter((img) => img !== objectData);
      state.sceneVideos = state.sceneVideos.filter((vid) => vid !== objectData);
      state.sceneAudios = state.sceneAudios.filter((aud) => aud !== objectData);
      state.sceneText = state.sceneText.filter((text) => text !== objectData);
    },

    update_assets_data: (state, action) => {
      const { type, data } = action.payload;

      // Keep existing behaviour for regular asset types
      state.assets_data = {
        ...state.assets_data,
        [type]: data,
      };

      if (
        [
          'image',
          'mesh',
          'video',
          'audio',
          'text',
          'hotspot',
          'empty',
        ].includes(type)
      ) {
        state[`assets_${type}_data`] = data;
      }

      // Ensure camera updates go to camera_properties as well
      if (type === 'camera') {
        state.camera_properties = data || [];
      }
    },

    addAsset: (state, action) => {
      const { arrKey, asset } = action.payload;
      state.assets_data = {
        ...state.assets_data,
        [arrKey]: [...(state.assets_data[arrKey] || []), asset],
      };
    },

    updateAssetVisibility: (state, action) => {
      const { arrKey, objectId, visible } = action.payload;
      const list = state.assets_data[arrKey];
      if (list) {
        state.assets_data = {
          ...state.assets_data,
          [arrKey]: list.map((asset) => {
            const matches =
              asset._id === objectId ||
              asset.id === objectId ||
              asset.name === objectId ||
              asset.url === objectId ||
              asset.temp_file_name === objectId ||
              asset.text_material?.id === objectId;
            return matches ? { ...asset, visible } : asset;
          }),
        };
      }
    },

    update_assets_data_new: (state, action) => {
      const { type, data } = action.payload;

      // Ensure it's a valid type
      if (
        ['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].includes(type)
      ) {
        const existing = state.assets_data[type] || [];

        // Append or merge new data (remove duplicates if needed)
        const merged = [...existing, ...data];

        // Update both master assets_data and type-specific one
        state.assets_data[type] = merged;
        state[`assets_${type}_data`] = merged;
      }
    },

    // update open modals

    updateOpenModals: (state, action) => {
      const { name, isOpen } = action.payload;
      state.openModals[name] = isOpen;
    },

    // Add or update camera_properties in the state
    // payload: single object { _id, type, near, far, fov?, fov_axis?, size?, name?, temp_file_name? }
    cameraPropertiesUpdate: (state, action) => {
      let updatedCamera = normalizeCameraFromApi(action.payload);
      if (!updatedCamera || typeof updatedCamera !== 'object') return;

      const updateId = getCameraStableId(updatedCamera);
      if (!updateId) return;
      updatedCamera = { ...updatedCamera, _id: updateId, id: updateId };

      const hasMatch = state.camera_properties.some(
        (cam) => cam._id === updateId || cam.id === updateId
      );
      if (hasMatch) {
        state.camera_properties = state.camera_properties.map((cam) =>
          cam._id === updateId || cam.id === updateId
            ? { ...cam, ...updatedCamera, _id: updateId, id: updateId }
            : cam
        );
      } else {
        state.camera_properties.push({ ...updatedCamera, _id: updateId, id: updateId });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handling API call pending state
      .addCase(get_grid_assets.pending, (state) => {
        state.assets_loading = true; // Set loading state to true
      })

      // Handling API call success response
      .addCase(get_grid_assets.fulfilled, (state, action) => {
        state.assets_loading = false;
        const res = action.payload;
        const condition = res.success;
        state.assets_data = condition ? res.data : {};
        state.assets_image_data = condition ? res.data.image : [];
        state.assets_video_data = condition ? res.data.video : [];
        state.assets_audio_data = condition ? res.data.audio : [];
        state.assets_mesh_data = condition ? res.data.mess : [];
      })

      // Handling API call failure response
      .addCase(get_grid_assets.rejected, (state) => {
        state.assets_loading = false; // Set loading state to false on failure
      })

      .addCase(add_new_assets.pending, (state) => {
        state.assets_loading = true; // Set loading state to true
      })
      .addCase(add_new_assets.fulfilled, (state, action) => {
        const payloadData = action.payload;
        const newId =
          payloadData?.data?._id ??
          payloadData?.data?.id ??
          payloadData?.data?.url ??
          payloadData?.data?.name;

        const existingList = state.assets_data?.[payloadData?.type] || [];
        const withoutDuplicate = newId
          ? existingList.filter(
              (item) =>
                (item?._id ?? item?.id ?? item?.url ?? item?.name) !== newId
            )
          : existingList;
        const merged = [...withoutDuplicate, payloadData.data];

        // ✅ First, add data inside the correct type inside assets_data (dedupe by id)
        state.assets_data = {
          ...(state.assets_data || {}),
          [payloadData?.type]: merged,
        };

        // ✅ Then, update the specific type array separately if needed
        ['image', 'mesh', 'video', 'audio', 'hotspot'].forEach((v) => {
          if (v === payloadData?.type) {
            const existingTyped = state[`assets_${v}_data`] || [];
            const withoutDup = newId
              ? existingTyped.filter(
                  (item) =>
                    (item?._id ?? item?.id ?? item?.url ?? item?.name) !== newId
                )
              : existingTyped;
            state[`assets_${v}_data`] = [...withoutDup, payloadData.data];
          }
        });

        state.assets_loading = false;
      })

      .addCase(add_new_assets.rejected, (state) => {
        state.assets_loading = false; // Set loading state to false on failure
      })

      // Remove Assets from the Array
      .addCase(remove_assets_by_id.fulfilled, (state, action) => {
        const payloadData = action.payload;
        state.assets_data = {
          ...(state.assets_data || {}),
          [payloadData?.type]: (
            state.assets_data?.[payloadData?.type] || []
          ).filter((item) => !assetIdMatchesPayload(item, payloadData)),
        };

        ['image', 'mesh', 'video', 'audio', 'hotspot', 'text'].forEach((v) => {
          if (v === payloadData?.type) {
            state[`assets_${v}_data`] = (
              state[`assets_${v}_data`] || []
            ).filter((item) => !assetIdMatchesPayload(item, payloadData));
          }
        });
        if (payloadData?.type === 'hotspot') {
          state.sceneHotspots = (state.sceneHotspots || []).filter(
            (obj) =>
              (obj?.id || obj?._id || obj?.url || obj?.name) !==
              (payloadData?.id || payloadData?.url)
          );
        }
      })
      .addCase(selected_object.fulfilled, (state, action) => {
        // const payloadData = action.payload;
        // state.select_object = payloadData;
        state.select_object = action.payload;
      });
  },
});
export const {
  set_is_glb_image_editoropen,
  set_is_video_editoropen,
  set_is_audio_editoropen,
  set_openEditorType,

  // new Reducers Export by naveen

  addImage,
  addModel,
  addVideo,
  addAudio,
  addSceneMesh,
  addSceneHotspot,
  removeSceneHotspot,
  addSceneImage,
  addSceneVideo,
  removeSceneVideo,
  addSceneAudio,
  clearAssets,
  removeSceneObject,
  setImageScales,
  setVideoScales,
  setAudioScales,
  addSceneText,
  setPushIntersectedObject,
  removeIntersectedObject,
  clearAllIntersectedObjects,
  update_assets_data,
  updateAssetVisibility,
  addAsset,
  update_assets_data_new,
  lightDataUpdate,
  updateOpenModals,
  updateCameraType,
  addCamera,
  removeCamera,
  cameraPropertiesUpdate, // Export the new reducer
  addEvent, // New export
  removeEvent, // New export
  clearEvents, // New export
  setSceneMeshes,
  updateSceneMeshMaterialSettings,
  restoreGridHistorySnapshot,
  setSelectedMeshesForGrouping,
  setMaterialHighlightMeshes,
  addLight,
  updateLight,
  removeLight,
  addSceneElement,
  addLightToOtherObject,
} = GridAssetsSlice.actions;
export default GridAssetsSlice.reducer;
